import { useState, useEffect, useCallback } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-slate-800 mt-1">{value || "—"}</div>
    </div>
  );
}

const NOTIF_CATEGORIES = [
  {
    key: "lead_message_email",
    label: "Lead message alerts",
    description: "Email me when an assigned lead sends a new inbound message.",
  },
  {
    key: "client_message_email",
    label: "Client message alerts",
    description: "Email me when an assigned client sends a new inbound message.",
  },
  {
    key: "conversation_assignment_email",
    label: "Conversation assignment alerts",
    description: "Email me when a lead or client conversation is newly assigned to me.",
  },
];

function NotificationPrefsSection() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null); // key of category being saved

  const loadPrefs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/staff-notification-prefs/me");
      setPrefs(data);
    } catch {
      toast.error("Failed to load notification preferences");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPrefs(); }, [loadPrefs]);

  const toggle = async (key) => {
    if (!prefs || saving) return;
    const newVal = !prefs[key];
    setPrefs((p) => ({ ...p, [key]: newVal }));
    setSaving(key);
    try {
      const { data } = await api.put("/staff-notification-prefs/me", { [key]: newVal });
      setPrefs(data);
      toast.success(newVal ? "Email alert enabled" : "Email alert disabled");
    } catch (err) {
      setPrefs((p) => ({ ...p, [key]: !newVal }));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update preference");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mt-5"
      data-testid="settings-notification-prefs"
    >
      <h2 className="font-heading font-medium text-slate-800 mb-1 flex items-center gap-2">
        <Icons.Bell className="h-4 w-4 text-slate-400" /> Notification Preferences
      </h2>
      <p className="text-sm text-slate-500 mb-5">
        Choose which events trigger an email alert to your inbox. All alerts are off by default.
        Email alerts never include message content or sensitive client data.
      </p>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm" data-testid="notif-prefs-loading">
          <Icons.Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
        </div>
      ) : (
        <div className="space-y-5">
          {NOTIF_CATEGORIES.map(({ key, label, description }) => (
            <div
              key={key}
              className="flex items-start justify-between gap-4"
              data-testid={`notif-pref-row-${key}`}
            >
              <div>
                <div className="text-sm font-medium text-slate-800">{label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{description}</div>
              </div>
              <div className="flex-shrink-0 pt-0.5">
                <Switch
                  checked={!!prefs?.[key]}
                  onCheckedChange={() => toggle(key)}
                  disabled={saving === key}
                  data-testid={`notif-pref-toggle-${key}`}
                  aria-label={label}
                />
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-5 flex items-center gap-1">
        <Icons.Info className="h-3 w-3" />
        Email alerts are sent immediately. Delivery requires the feature to be activated by your administrator.
      </p>
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwd.new_password !== pwd.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (pwd.new_password.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current_password,
        new_password: pwd.new_password,
      });
      toast.success("Password updated");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-3xl">
      <div className="mb-6">
        <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-slate-500 mt-1">Manage your profile, security, and notification preferences.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-5" data-testid="settings-profile">
        <h2 className="font-heading font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Icons.User className="h-4 w-4 text-slate-400" /> Profile
        </h2>
        <div className="grid grid-cols-2 gap-5">
          <Field label="Name" value={user?.name} />
          <Field label="Email" value={user?.email} />
          <Field label="Role" value={user?.role_label} />
          <Field label="Team" value={user?.team?.replace(/_/g, " ")} />
          <Field label="Title" value={user?.title} />
          <Field label="Last login" value={user?.last_login_at ? new Date(user.last_login_at).toLocaleString() : "—"} />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6" data-testid="settings-security">
        <h2 className="font-heading font-medium text-slate-800 mb-4 flex items-center gap-2">
          <Icons.Lock className="h-4 w-4 text-slate-400" /> Change Password
        </h2>
        <form onSubmit={changePassword} className="space-y-3 max-w-sm">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Current password</label>
            <input type="password" required className={inputCls} value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="current-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">New password</label>
            <input type="password" required className={inputCls} value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="new-password" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Confirm new password</label>
            <input type="password" required className={inputCls} value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="confirm-password" />
          </div>
          <button type="submit" disabled={saving} data-testid="change-password-submit" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg px-4 py-2 text-sm font-medium">
            {saving && <Icons.Loader2 className="h-4 w-4 animate-spin" />} Update Password
          </button>
        </form>
      </div>

      <NotificationPrefsSection />
    </div>
  );
}
